import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail, MapPin, Linkedin, Instagram } from 'lucide-react';
import NewsletterForm from './NewsletterForm';

const BOOK_PAGE_LINK = "https://calendly.com/nicolas-gate33/discovery-call";

// Social links — keep in sync with airline landing pages & job-board page
const SOCIAL_LINKS = {
  whatsapp: 'https://chat.whatsapp.com/EMMoeIfWQcgIhuH4Dz49mE',
  linkedin: 'https://www.linkedin.com/company/gate-33/',
  instagram: 'https://www.instagram.com/nicolasgate33',
  tiktok: 'https://www.tiktok.com/@nicolasgate33',
};

// Inline brand glyphs — match lucide stroke style at 1.5 weight
const WhatsAppGlyph = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 .07 5.3.07 11.93c0 2.1.55 4.16 1.6 5.98L0 24l6.27-1.64a11.93 11.93 0 0 0 5.73 1.46h.01c6.62 0 11.92-5.3 11.92-11.93 0-3.19-1.24-6.18-3.41-8.41z" />
    <path d="M17.4 14.4c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.18.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.31 1.26.49 1.69.62.71.22 1.36.19 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35z" />
  </svg>
);

const TikTokGlyph = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 8.5a7 7 0 0 1-4-1.27V16a5.5 5.5 0 1 1-5.5-5.5h.75v3.25h-.75A2.25 2.25 0 1 0 13.75 16V2h3a4.5 4.5 0 0 0 4.25 4.5z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    navigation: [
      { name: 'Home', path: '/' },
      { name: '3-Day Online Training', path: '/academy' },
      { name: 'About', path: '/about' },
      { name: 'Contact', path: '/contact' },
    ],
    services: [
      { name: '3-Day Online Training', path: '/academy', hint: '3 live online days with Nicolas · €600' },
      { name: 'Mentoring', path: '/mentorship' },
      { name: 'B2B Consulting', path: '/consulting' },
    ],
    resources: [
      { name: 'Free Masterclass', path: '/masterclass' },
      { name: 'Careers', path: '/careers' },
      { name: 'Airline Transitions', path: '/transition' },
      { name: 'Insights', path: '/insights' },
      { name: 'FAQ', path: '/faq' },
    ],
    legal: [
      { name: 'Legal Notice', path: '/legal/mentions-legales' },
      { name: 'Terms of Sale (CGV)', path: '/legal/terms' },
      { name: 'Privacy (RGPD)', path: '/legal/privacy' },
      { name: 'Cookies', path: '/legal/cookies' },
      { name: 'Delivery & Returns', path: '/legal/delivery-returns' },
      { name: 'Withdrawal Form', path: '/legal/withdrawal-form' },
    ],
  };

  return (
    <footer data-testid="footer" className="bg-[#0F0F0F] text-[#FDFBF7]">
      {/* Newsletter capture — top of footer */}
      <div data-testid="footer-newsletter-block" className="border-b border-[#C5A880]/25">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-3">
              Newsletter
            </span>
            <h3 className="font-heading text-2xl sm:text-3xl text-[#FDFBF7] leading-tight">
              Get operator news, cohort dates &amp; career insights.
            </h3>
            <p className="text-sm text-[#9A9A9A] mt-3 max-w-lg">
              Sent by Nicolas. Low frequency, no spam — unsubscribe anytime.
            </p>
          </div>
          <NewsletterForm source="footer" variant="dark" compact testId="footer-newsletter" />
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand + Contact Info */}
          <div className="md:col-span-4">
            <Link
              to="/"
              data-testid="footer-logo"
              className="font-heading text-3xl font-semibold tracking-tight inline-block mb-6 text-[#FDFBF7]"
            >
              Gate <span className="text-[#C5A880]">33</span>
            </Link>
            <p className="text-[#9A9A9A] text-sm leading-relaxed mb-8">
              Your strategic gateway into private aviation. Expert guidance for crew, hospitality professionals and career changers transitioning to business aviation.
            </p>

            {/* Get In Touch - under GATE 33 logo */}
            <h4 className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C5A880] mb-4">
              Get in Touch
            </h4>
            <div className="space-y-3">
              <a
                href="mailto:nicolas@gate33.co"
                data-testid="footer-email"
                className="flex items-center gap-3 text-[#9A9A9A] hover:text-[#FDFBF7] transition-colors"
              >
                <Mail size={14} className="text-[#C5A880]" />
                <span className="text-sm">nicolas@gate33.co</span>
              </a>
              <div className="flex items-center gap-3 text-[#9A9A9A]">
                <MapPin size={14} className="text-[#C5A880]" />
                <span className="text-sm">Gate 33 is LFMN based</span>
              </div>
            </div>

            {/* Social icons */}
            <div className="mt-6 flex items-center gap-3" data-testid="footer-social-icons">
              <a
                href={SOCIAL_LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Join the Gate 33 WhatsApp community"
                data-testid="footer-social-whatsapp"
                className="w-9 h-9 inline-flex items-center justify-center border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-colors duration-300"
              >
                <WhatsAppGlyph size={15} />
              </a>
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Gate 33 on LinkedIn"
                data-testid="footer-social-linkedin"
                className="w-9 h-9 inline-flex items-center justify-center border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-colors duration-300"
              >
                <Linkedin size={15} strokeWidth={1.5} />
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="@nicolasgate33 on Instagram"
                data-testid="footer-social-instagram"
                className="w-9 h-9 inline-flex items-center justify-center border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-colors duration-300"
              >
                <Instagram size={15} strokeWidth={1.5} />
              </a>
              <a
                href={SOCIAL_LINKS.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="@nicolasgate33 on TikTok"
                data-testid="footer-social-tiktok"
                className="w-9 h-9 inline-flex items-center justify-center border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-colors duration-300"
              >
                <TikTokGlyph size={15} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A880] mb-6">
              Navigate
            </h4>
            <ul className="space-y-3">
              {footerLinks.navigation.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-[#9A9A9A] text-sm hover:text-[#FDFBF7] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A880] mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/ /g, '-')}`}
                    className="text-[#9A9A9A] text-sm hover:text-[#FDFBF7] transition-colors block"
                  >
                    {link.name}
                    {link.hint && (
                      <span className="block text-[10px] uppercase tracking-[0.15em] text-[#5A5A5A] mt-0.5">
                        {link.hint}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A880] mb-6">
              Resources
            </h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-resource-${link.name.toLowerCase().replace(/ /g, '-')}`}
                    className="text-[#9A9A9A] text-sm hover:text-[#FDFBF7] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C5A880] mb-6">
              Ready to Start?
            </h4>
            <p className="text-[#9A9A9A] text-sm mb-6">
              Book a free discovery call and take the first step into private aviation.
            </p>
            <a
              href={BOOK_PAGE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta-book"
              className="inline-flex items-center gap-2 border border-[#C5A880] text-[#C5A880] px-6 py-3 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
            >
              Book Discovery Call
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>

      {/* Gold separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="border-t border-[#C5A880]/40" />
      </div>

      {/* Legal bar — visible on every page for LCEN / RGPD compliance */}
      <div data-testid="footer-legal-bar" className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <div className="flex flex-col gap-4">
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[#9A9A9A]">
            {footerLinks.legal.map((link) => (
              <li key={link.name}>
                <Link
                  to={link.path}
                  data-testid={`footer-legal-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="hover:text-[#C5A880] transition-colors uppercase tracking-[0.1em]"
                >
                  {link.name}
                </Link>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new Event('gate33:open-cookie-preferences'));
                  }
                }}
                data-testid="footer-legal-cookie-preferences"
                className="hover:text-[#C5A880] transition-colors uppercase tracking-[0.1em]"
              >
                Cookie preferences
              </button>
            </li>
          </ul>
          <p className="text-[10px] text-[#6A6A6A] leading-relaxed">
            NPDS · SIREN 534 221 387 · SIRET 534 221 387 000 28 · TVA FR06 534 221 387 · Publication director: Nicolas Perot dos Santos · Contact: <a href="mailto:nicolas@gate33.co" className="hover:text-[#C5A880] transition-colors">nicolas@gate33.co</a>
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#6A6A6A] text-xs">
              &copy; {currentYear} Gate 33. All rights reserved.
            </p>
            <p className="text-[#6A6A6A] text-xs">
              Elevating careers in private aviation.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
